import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  TextField,
  IconButton,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  Phone as PhoneIcon,
  Info as InfoIcon,
  AddCircleOutline as AddCircleIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { Socket } from 'socket.io-client';

interface Message {
  id: string;
  from: string;
  to: string;
  body: string;
  direction: 'inbound' | 'outbound';
  timestamp: string;
}

interface ConversationDetailProps {
  phoneNumber: string;
  contactName?: string;
  twilioNumber: string;
  socket: Socket | null;
}

const ConversationDetail: React.FC<ConversationDetailProps> = ({
  phoneNumber,
  contactName,
  twilioNumber,
  socket
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMessages(true);
    // Poll for new messages every 8 seconds
    const interval = setInterval(() => {
      loadMessages(false);
    }, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  useEffect(() => {
    if (socket) {
      socket.on('newMessage', handleNewMessage);
      return () => { socket.off('newMessage', handleNewMessage); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, phoneNumber]);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages.length]);

  const loadMessages = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');
      const response = await axios.get(
        `/api/conversations/${encodeURIComponent(phoneNumber)}?twilioNumber=${encodeURIComponent(twilioNumber)}`
      );
      setMessages(response.data.conversation?.messages || []);
    } catch (err: any) {
      console.error('Error loading conversation:', err);
      setError(err.response?.data?.error || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message: any) => {
    const messagePhone = message.direction === 'inbound' ? message.from : message.to;
    if (messagePhone === phoneNumber) {
      setMessages(prev => [...prev, message]);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || sending) return;
    try {
      setSending(true);
      await axios.post('/api/messages/send', { to: phoneNumber, body: messageText.trim(), from: twilioNumber });
      setMessageText('');
      await loadMessages();
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateDivider = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) return 'TODAY';
    return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase();
  };

  const charCount = messageText.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #e8e8ef', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#6c5ce7', width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
            {messages.length}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#2d3436', lineHeight: 1.2 }}>
              {contactName || phoneNumber}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              Status: Active • Assigned to: {JSON.parse(localStorage.getItem('user') || '{}').loginId || 'You'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" sx={{ color: '#999' }}><PhoneIcon fontSize="small" /></IconButton>
          <IconButton size="small" sx={{ color: '#999' }}><InfoIcon fontSize="small" /></IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2, flexShrink: 0 }}>{error}</Alert>}

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">No messages yet. Start the conversation below.</Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const isOutbound = message.direction === 'outbound';
            const showDateDivider = index === 0 ||
              new Date(messages[index - 1].timestamp).toDateString() !== new Date(message.timestamp).toDateString();

            return (
              <React.Fragment key={message.id}>
                {showDateDivider && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Chip
                      label={formatDateDivider(message.timestamp)}
                      size="small"
                      sx={{ bgcolor: '#f0f0f0', color: '#888', fontSize: 10, fontWeight: 600, letterSpacing: 0.5, height: 22 }}
                    />
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOutbound ? 'flex-end' : 'flex-start', mb: 1 }}>
                  <Box
                    sx={{
                      maxWidth: '70%',
                      px: 2,
                      py: 1.5,
                      borderRadius: isOutbound ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      bgcolor: isOutbound ? '#6c5ce7' : '#f0f0f5',
                      color: isOutbound ? '#fff' : '#2d3436',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                      {message.body}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: 10 }}>
                      {formatTime(message.timestamp)}
                    </Typography>
                    {isOutbound && <CheckCircleIcon sx={{ fontSize: 12, color: '#6c5ce7' }} />}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input */}
      <Box sx={{ borderTop: '1px solid #e8e8ef', px: 3, py: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" sx={{ color: '#999' }}><AddCircleIcon /></IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f5f5f9',
                '& fieldset': { border: 'none' },
              }
            }}
          />
          <Typography variant="caption" sx={{ color: '#999', whiteSpace: 'nowrap', fontSize: 11 }}>
            {charCount}/160
          </Typography>
          <IconButton
            onClick={handleSendMessage}
            disabled={!messageText.trim() || sending}
            sx={{
              bgcolor: '#6c5ce7',
              color: '#fff',
              width: 36,
              height: 36,
              '&:hover': { bgcolor: '#5a4bd1' },
              '&.Mui-disabled': { bgcolor: '#ddd', color: '#999' }
            }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
        {/* Bottom toolbar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { color: '#6c5ce7' } }}>
              <DescriptionIcon sx={{ fontSize: 14, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>TEMPLATES</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { color: '#6c5ce7' } }}>
              <ScheduleIcon sx={{ fontSize: 14, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>SCHEDULE</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>
            CARRIER: CONNECTPIE SMS
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationDetail;
